import React, { useCallback, useEffect, useState } from 'react';
import { tenantAPI } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { apiError } from '../lib/host';
import './AdminDashboard.css';

export default function Settings() {
  const { tenant, can, reloadTenant } = useTenant();
  const [name, setName] = useState(tenant.name);
  const [flags, setFlags] = useState([]);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    tenantAPI.features().then((res) => setFlags(res.data)).catch((err) => setError(apiError(err)));
    if (can('audit.read')) tenantAPI.auditLogs().then((res) => setLogs(res.data)).catch(() => {});
  }, [can]);

  useEffect(load, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      if (ok) setNotice(ok);
      load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const flagValue = (f) => (f.overridden ? String(f.enabled) : 'inherit');

  return (
    <>
      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="tcx-success">{notice}</div>}

      <section className="tcx-panel">
        <h3>Organization</h3>
        <form className="tcx-row" onSubmit={(e) => { e.preventDefault(); run(async () => { await tenantAPI.rename(name); await reloadTenant(); }, 'Name updated'); }}>
          <div className="form-group">
            <label>Name</label>
            <input className="tcx-input" value={name} minLength={2} required onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-group">
            <label>URL</label>
            <input className="tcx-input" value={tenant.slug} disabled />
          </div>
          <button className="tcx-btn" disabled={busy}>Save</button>
        </form>
      </section>

      <section className="tcx-panel">
        <h3>Feature flags</h3>
        <p className="tcx-muted" style={{ marginBottom: 12 }}>
          Overrides are stored in this organization's database. "Inherit" uses the platform default.
        </p>
        <table className="tcx-table">
          <thead><tr><th>Flag</th><th>Platform default</th><th>This organization</th></tr></thead>
          <tbody>
            {flags.filter((f) => f.name !== 'registration_enabled').map((f) => (
              <tr key={f.name}>
                <td>{f.description || f.name}</td>
                <td>{f.platform_default ? 'On' : 'Off'}</td>
                <td>
                  <select className="tcx-select" value={flagValue(f)} disabled={busy || !can('flags.write')}
                    onChange={(e) => {
                      const v = e.target.value;
                      run(() => tenantAPI.setFeature(f.name, v === 'inherit' ? null : v === 'true'));
                    }}>
                    <option value="inherit">Inherit ({f.platform_default ? 'on' : 'off'})</option>
                    <option value="true">On</option>
                    <option value="false">Off</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {can('audit.read') && (
        <section className="tcx-panel">
          <h3>Audit log</h3>
          <div className="tcx-table-wrap">
            <table className="tcx-table">
              <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th><th>Details</th></tr></thead>
              <tbody>
                {logs.length === 0 && <tr><td colSpan={5} className="tcx-muted">No activity yet</td></tr>}
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td>{new Date(l.created_at + 'Z').toLocaleString()}</td>
                    <td>#{l.actor_user_id}</td>
                    <td>{l.action}</td>
                    <td>{l.target || '—'}</td>
                    <td><code style={{ fontSize: 12 }}>{l.details ? JSON.stringify(l.details) : ''}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
